import { useCallback, useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import Modal from '@components/shared/Modal'
import JurisprudenceProviders from '@services/jurisprudenceProviders'
import { officialProviderUrl, PROVIDER_NUMBER_FIELDS, providerFields, providerFieldsChanged,
  providerMutationError, providerUpdatePayload, validateProviderFields } from './jurisprudenceProviderHelpers'
import './pecasAcervo.css'
import './jurisprudenceProviders.css'

const entryFromProvider = (provider) => ({ provider, fields: providerFields(provider), error: '', success: '', conflict: false, busy: false })
const updatedLabel = (value) => {
  const date = new Date(value)
  return value && !Number.isNaN(date.valueOf()) ? date.toLocaleString('pt-BR') : null
}

function ProviderCard({ entry, onChange, onSave, onReload }) {
  const { provider, fields, error, success, conflict, busy } = entry
  const dirty = providerFieldsChanged(provider, fields)
  const errors = validateProviderFields(provider, fields)
  const sourceUrl = officialProviderUrl(provider.officialDiscoveryUrl)
  const id = `jurisprudence-provider-${provider.providerId}`
  const updated = updatedLabel(provider.updatedAt)
  return (
    <form className="jurisprudence-provider-card" aria-labelledby={`${id}-title`} aria-busy={busy}
      onSubmit={(event) => { event.preventDefault(); onSave() }}>
      <div className="jurisprudence-provider-heading">
        <div>
          <h3 id={`${id}-title`}>{provider.name}</h3>
          <p className="acervo-muted">{provider.courts?.join(', ') || provider.courtFamily}
            {provider.qualifiedPrecedentsOnly ? ' · Precedentes qualificados' : ''}</p>
        </div>
        <span className={`acervo-status ${provider.enabled ? 'acervo-status-pronto' : ''}`}>
          {provider.enabled ? 'Ativa' : 'Desativada'}
        </span>
      </div>
      <div className="jurisprudence-provider-capabilities">
        <span><i className={`fas ${provider.supportsAutomaticSearch ? 'fa-magnifying-glass' : 'fa-hand-pointer'}`} aria-hidden="true" />
          {provider.supportsAutomaticSearch ? 'Busca automática disponível' : 'Busca manual'}</span>
        <span><i className={`fas ${provider.supportsAutomaticFullText ? 'fa-file-lines' : 'fa-hand-pointer'}`} aria-hidden="true" />
          {provider.supportsAutomaticFullText ? 'Leitura automática disponível' : 'Leitura assistida'}</span>
      </div>
      {provider.limitation ? <p id={`${id}-limitation`} className="jurisprudence-provider-limitation">{provider.limitation}</p> : null}
      {sourceUrl ? <a className="jurisprudence-provider-source" href={sourceUrl} target="_blank" rel="noopener noreferrer">
        Consultar fonte oficial <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
      </a> : null}
      <fieldset className="jurisprudence-provider-fields" disabled={busy}>
        <legend className="acervo-sr-only">Configuração de {provider.name}</legend>
        <label className="jurisprudence-provider-check jurisprudence-provider-enable">
          <input type="checkbox" checked={fields.enabled} onChange={(event) => onChange('enabled', event.target.checked)} />
          <span><strong>Ativar integração</strong><small>Permite utilizar esta fonte nas pesquisas de jurisprudência.</small></span>
        </label>
        <div className="jurisprudence-provider-operation">
          <label className="acervo-field" htmlFor={`${id}-search`}>
            Modo de busca
            <select id={`${id}-search`} value={fields.searchMode} onChange={(event) => onChange('searchMode', event.target.value)}
              aria-describedby={`${id}-limitation`} aria-invalid={Boolean(errors.searchMode)}>
              <option value="Manual">Manual</option>
              <option value="Automatic" disabled={!provider.supportsAutomaticSearch}>Automática</option>
            </select>
            <small>{provider.supportsAutomaticSearch
              ? 'No modo automático, o worker consulta esta fonte durante a pesquisa.'
              : 'A busca automática depende de um conector disponível para esta fonte.'}</small>
            {errors.searchMode ? <small className="jurisprudence-provider-invalid">{errors.searchMode}</small> : null}
          </label>
          <label className="jurisprudence-provider-check">
            <input type="checkbox" checked={fields.automaticFullText} disabled={!provider.supportsAutomaticFullText}
              onChange={(event) => onChange('automaticFullText', event.target.checked)} aria-describedby={`${id}-limitation`} />
            <span><strong>Ler inteiro teor automaticamente</strong><small>{provider.supportsAutomaticFullText
              ? 'O worker extrai o texto da fonte oficial quando houver documento acessível. A leitura manual por link permanece disponível.'
              : 'A leitura automática ainda não está disponível para esta fonte.'}</small></span>
          </label>
        </div>
        <div className="jurisprudence-provider-limits">
          {PROVIDER_NUMBER_FIELDS.map(({ key, label, min, max, hint }) => (
            <label key={key} className="acervo-field" htmlFor={`${id}-${key}`}>
              {label}
              <input id={`${id}-${key}`} type="number" inputMode="numeric" min={min} max={max} step="1"
                value={fields[key]} onChange={(event) => onChange(key, event.target.value)}
                aria-invalid={Boolean(errors[key])} aria-describedby={`${id}-${key}-hint`} required />
              <small id={`${id}-${key}-hint`} className={errors[key] ? 'jurisprudence-provider-invalid' : ''}>{errors[key] || hint}</small>
            </label>
          ))}
        </div>
      </fieldset>
      {error ? <div className="acervo-notice acervo-error" role="alert">{error}</div> : null}
      {success ? <div className="acervo-notice acervo-success" role="status">{success}</div> : null}
      <div className="jurisprudence-provider-footer">
        <div className="jurisprudence-provider-save-state" aria-live="polite">
          {dirty ? <strong>Alterações não salvas</strong> : <span>Configuração salva</span>}
          {updated ? <small>Atualizada em {updated}</small> : null}
        </div>
        <div className="acervo-toolbar-actions">
          <button className="acervo-button" type="button" disabled={busy} onClick={onReload}>
            <i className="fas fa-rotate" aria-hidden="true" /> Recarregar
          </button>
          <button className="acervo-button acervo-primary" type="submit" disabled={busy || !dirty || conflict || Object.keys(errors).length > 0}>
            <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" />
            {busy ? 'Aguarde…' : 'Salvar integração'}
          </button>
        </div>
      </div>
    </form>
  )
}

export default function JurisprudenceProvidersPanel() {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)
  const [pendingExit, setPendingExit] = useState(null)
  const [pendingReload, setPendingReload] = useState(null)
  const operations = useRef(new Map())
  const hasWork = entries.some((entry) => entry.busy || providerFieldsChanged(entry.provider, entry.fields))
  const blocker = useBlocker(({ currentLocation, nextLocation }) => hasWork &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search || currentLocation.hash !== nextLocation.hash))
  const changeEntry = useCallback((providerId, update) => {
    setEntries((current) => current.map((entry) => entry.provider.providerId === providerId ? update(entry) : entry))
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError('')
    JurisprudenceProviders.list(controller.signal).then((response) => {
      if (controller.signal.aborted) return
      if (response.success && Array.isArray(response.data)) setEntries(response.data.map(entryFromProvider))
      else setError(response.error || 'Não foi possível carregar as integrações de jurisprudência.')
      setLoading(false)
    })
    return () => controller.abort()
  }, [revision])

  useEffect(() => {
    const currentOperations = operations.current
    return () => { currentOperations.forEach((controller) => controller.abort()) }
  }, [])
  useEffect(() => {
    if (!hasWork) return undefined
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = '' }
    const beforeLogout = (event) => {
      if (typeof event.detail?.proceed !== 'function') return
      event.preventDefault()
      setPendingExit({ proceed: event.detail.proceed })
    }
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('auth:before-logout', beforeLogout)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('auth:before-logout', beforeLogout)
    }
  }, [hasWork])

  const updateField = (providerId, key, value) => changeEntry(providerId, (entry) => ({
    ...entry, fields: { ...entry.fields, [key]: value }, success: '', error: entry.conflict ? entry.error : '',
  }))
  const save = async (entry) => {
    const { provider, fields } = entry
    if (operations.current.has(provider.providerId) || entry.conflict || Object.keys(validateProviderFields(provider, fields)).length) return
    const controller = new AbortController()
    operations.current.set(provider.providerId, controller)
    changeEntry(provider.providerId, (current) => ({ ...current, busy: true, error: '', success: '' }))
    try {
      const response = await JurisprudenceProviders.update(provider.providerId, providerUpdatePayload(provider, fields), controller.signal)
      if (controller.signal.aborted) return
      if (response.success && response.data?.providerId === provider.providerId) {
        changeEntry(provider.providerId, () => ({ ...entryFromProvider(response.data), success: 'Configuração salva.' }))
      } else changeEntry(provider.providerId, (current) => ({ ...current, error: providerMutationError(response), conflict: response.status === 409 }))
    } catch {
      if (!controller.signal.aborted) changeEntry(provider.providerId, (current) => ({ ...current, error: 'Não foi possível confirmar o salvamento. Recarregue a configuração para conferir o estado atual.' }))
    } finally {
      operations.current.delete(provider.providerId)
      if (!controller.signal.aborted) changeEntry(provider.providerId, (current) => ({ ...current, busy: false }))
    }
  }
  const reload = async (providerId) => {
    if (operations.current.has(providerId)) return
    setPendingReload(null)
    const controller = new AbortController()
    operations.current.set(providerId, controller)
    changeEntry(providerId, (current) => ({ ...current, busy: true, error: '', success: '' }))
    try {
      const response = await JurisprudenceProviders.list(controller.signal)
      if (controller.signal.aborted) return
      const provider = response.success && Array.isArray(response.data) && response.data.find((item) => item.providerId === providerId)
      if (provider) changeEntry(providerId, () => ({ ...entryFromProvider(provider), success: 'Configuração salva recarregada.' }))
      else changeEntry(providerId, (current) => ({ ...current, error: response.error || 'Não foi possível recarregar esta integração. Suas edições foram mantidas.' }))
    } catch {
      if (!controller.signal.aborted) changeEntry(providerId, (current) => ({ ...current, error: 'Não foi possível recarregar. Suas edições foram mantidas.' }))
    } finally {
      operations.current.delete(providerId)
      if (!controller.signal.aborted) changeEntry(providerId, (current) => ({ ...current, busy: false }))
    }
  }
  const cancelExit = () => {
    setPendingExit(null)
    if (blocker.state === 'blocked') blocker.reset()
  }
  const confirmExit = () => {
    operations.current.forEach((controller) => controller.abort())
    const proceed = pendingExit?.proceed
    setPendingExit(null)
    if (blocker.state === 'blocked') blocker.proceed()
    else proceed?.()
  }

  return (
    <section className="acervo-section jurisprudence-providers" aria-labelledby="jurisprudence-providers-title">
      <div className="acervo-toolbar acervo-section-header">
        <div className="acervo-section-heading">
          <h2 id="jurisprudence-providers-title">Integrações de jurisprudência</h2>
          <p>Configure as fontes oficiais usadas na pesquisa e na leitura do inteiro teor. As alterações valem para todos os usuários.</p>
        </div>
        <button className="acervo-button" type="button" disabled={loading || hasWork} onClick={() => setRevision((value) => value + 1)}>
          <i className="fas fa-rotate" aria-hidden="true" /> Atualizar lista
        </button>
      </div>
      <p className="jurisprudence-providers-note">Busca e leitura são configurações independentes. A automação utiliza os recursos disponíveis de cada fonte e depende da pesquisa jurisprudencial estar habilitada no sistema.</p>
      {loading ? <p className="acervo-working" role="status"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Carregando integrações…</p> : null}
      {error ? <div className="acervo-notice acervo-error" role="alert">{error}</div> : null}
      {!loading && !error && entries.length === 0 ? <div className="acervo-empty">Nenhuma integração cadastrada. Confira a configuração do catálogo no servidor.</div> : null}
      {!loading && entries.length > 0 ? <div className="jurisprudence-provider-list">
        {entries.map((entry) => <ProviderCard key={entry.provider.providerId} entry={entry}
          onChange={(key, value) => updateField(entry.provider.providerId, key, value)} onSave={() => save(entry)}
          onReload={() => providerFieldsChanged(entry.provider, entry.fields) ? setPendingReload(entry.provider) : reload(entry.provider.providerId)} />)}
      </div> : null}
      <Modal isOpen={Boolean(pendingReload)} onClose={() => setPendingReload(null)} title="Recarregar configuração?" size="small"
        footer={<><button className="btn btn-outline" onClick={() => setPendingReload(null)}>Continuar editando</button>
          <button className="btn btn-danger" onClick={() => reload(pendingReload.providerId)}>Descartar e recarregar</button></>}>
        <p>As alterações não salvas de {pendingReload?.name} serão substituídas pela configuração atual do servidor. As demais integrações serão preservadas.</p>
      </Modal>
      <Modal isOpen={Boolean(pendingExit) || blocker.state === 'blocked'} onClose={cancelExit} title="Sair das configurações?" size="small"
        footer={<><button className="btn btn-outline" onClick={cancelExit}>Continuar editando</button>
          <button className="btn btn-danger" onClick={confirmExit}>Sair sem salvar</button></>}>
        <p>Há alterações não salvas ou uma operação em andamento. Ao sair, as edições locais serão descartadas. Uma configuração já recebida pelo servidor pode ter sido salva.</p>
      </Modal>
    </section>
  )
}
